/**
 * Combined auth facade: composes Azure and Salesforce auth contexts.
 * Exposes a single AuthContextValue for backward compatibility (useAuth, RequireAuth, Login, etc.).
 * Must be used inside AzureAuthProvider and/or SalesforceAuthProvider depending on config.
 */

import React, { createContext, useContext, useMemo } from "react";
import { storage, type TokenSet } from "./storage";
import { isAzureLoginEnabled, isSfLoginEnabled } from "./azureConfig";
import { useOptionalAzureAuth } from "./AzureAuthContext";
import { useOptionalSalesforceAuth } from "./SalesforceAuthContext";

type User = Record<string, unknown> | null;

export type AuthContextValue = {
  isAuthenticated?: boolean;
  /** Salesforce access token (for SF API calls). */
  accessToken?: string;
  /** MSAL access token for backend API calls (Azure auth only). Use for Authorization header. */
  backendAccessToken?: string;
  instanceUrl?: string;
  idToken?: string;
  user: User;
  isUserLoading: boolean;
  login: (interactive?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  applySfTokenSet: (tokenSet: TokenSet) => void;
  applyAzureTokenSet: (tokenSet: TokenSet) => void;
  logoutAppOnly: () => void;
};

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

function CombinedAuthProviderInner({
  children,
}: {
  children: React.ReactNode;
}) {
  const azure = useOptionalAzureAuth();
  const sf = useOptionalSalesforceAuth();

  const azureEnabled = isAzureLoginEnabled();
  const sfEnabled = isSfLoginEnabled();

  const value = useMemo<AuthContextValue>(() => {
    const azureOk = !azureEnabled || azure?.isAuthenticated;
    const sfOk = !sfEnabled || sf?.isAuthenticated;
    const isAuthenticated = azureOk && sfOk;

    const login = async (interactive?: boolean) => {
      if (azureEnabled && azure?.login) {
        await azure.login(interactive);
      }
      // Salesforce login is done via redirect (buildAuthorizeUrl) from Login page
    };

    const logout = async () => {
      // Clear both Azure and Salesforce auth sessions
      storage.clearTemp();
      storage.clearAllAuthSessions();

      if (azureEnabled && azure?.logout) await azure.logout();
      if (sfEnabled && sf?.logout) await sf.logout();
    };

    const logoutAppOnly = () => {
      if (azureEnabled && azure?.logoutAppOnly) azure.logoutAppOnly();
      if (sfEnabled && sf?.logoutAppOnly) sf.logoutAppOnly();
    };

    const refresh = async () => {
      if (azure?.refresh) await azure.refresh();
      if (sf?.refresh) await sf.refresh();
    };

    const applySfTokenSet = (tokenSet: TokenSet) => {
      // Callback route applies SF token set
      if (sf?.applyTokenSet) sf.applyTokenSet(tokenSet);
    };
    const applyAzureTokenSet = (tokenSet: TokenSet) => {
      // Callback route applies Azure token set
      if (azure?.applyTokenSet) azure.applyTokenSet(tokenSet);
    };

    return {
      sf,
      azure,
      isAuthenticated,
      accessToken: sf?.accessToken,
      backendAccessToken: azure?.accessToken,
      instanceUrl: sf?.instanceUrl,
      idToken: sf?.idToken ?? azure?.idToken,
      user: (sf?.user ?? azure?.user ?? null) as User,
      isUserLoading: Boolean(azure?.isUserLoading || sf?.isUserLoading),
      login,
      logout,
      refresh,
      applySfTokenSet,
      applyAzureTokenSet,
      logoutAppOnly,
    };
  }, [azureEnabled, sfEnabled, azure, sf]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Combined auth provider. Wrap app with this after AzureAuthProvider and SalesforceAuthProvider
 * so that useAuth() returns merged state from both contexts.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  return <CombinedAuthProviderInner>{children}</CombinedAuthProviderInner>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function useOptionalAuth(): AuthContextValue | undefined {
  return useContext(AuthContext);
}
